import React, { useEffect, useState } from 'react';
import {Card, Typography, Container, CardMedia, Box, Button} from '@mui/material';
import LocalLoader from "../Loaders/LocalLoader.js";
import { KBImageComponent } from "./KBImage.js";
import {isMobile, parkedDomain} from "../App/utils";
import {KBNamePlural} from "../names";
import { listApps, getPublishedKBs } from "../API/APP/AppAPI";
import {colorGray, colorText} from "../App/theme";
import ThumbUpOffAltIcon from '@mui/icons-material/FavoriteBorderOutlined';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import { formatNumber } from "./utils";
import { Link, useNavigate } from 'react-router-dom';
import {RAW_FILES_URL} from "../utils";

export const AppCenter = () => {
    const [apps, setApps] = useState([]);
    const [publishedKBs, setPublishedKBs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleNavigateWrapper = (event, path) => {
        event.preventDefault();
        if (event?.ctrlKey || event?.metaKey) {
            window.open(path, '_blank');
            return;
        } else {
            navigate(path);
        }
    };

    useEffect(() => {
        const fetchApps = async () => {
            setIsLoading(true);
            try {
                const orderBy = 'likes';
                const orderDirection = 'desc';
                const appsData = await listApps(orderBy, orderDirection);
                // Filter out hidden blueprints
                const hiddenAppIds = ['bb2f9a1gd1c'];
                const filteredApps = appsData.filter(app =>
                    !hiddenAppIds.includes(app.appId)
                );
                setApps(filteredApps);
            } catch (error) {
                console.error('Error fetching apps:', error);
            }
            setIsLoading(false);
        };

        const fetchPublishedKBs = async () => {
            try {
                // Use parkedDomain if available, otherwise default to 'openkbs.com'
                const domain = parkedDomain || 'openkbs.com';
                const kbsData = await getPublishedKBs(domain);
                setPublishedKBs(Array.isArray(kbsData) ? kbsData : []);
            } catch (error) {
                console.error('Error fetching published KBs:', error);
                setPublishedKBs([]);
            }
        };

        fetchApps();
        fetchPublishedKBs();
    }, []);

    const imageSize = isMobile ? '80px' : '120px';

    const iconFontSize = isMobile ? 10 : 'inherit';

    const containerStyle = isMobile ? {marginLeft: '-5%', width: '110%'} : {};

    return (
        <Container style={containerStyle}>
            {/* Published KBs Section */}
            {publishedKBs.length > 0 && (
                <>
                    <Typography variant="overline" display="block"
                                style={{color: colorText, opacity: 0.5, fontSize: 12, letterSpacing: 2, marginTop: 20, marginBottom: 12}}>
                        {'Published ' + KBNamePlural}
                    </Typography>
                    <Box mt={-2} mb={4} display="flex" flexWrap="wrap" justifyContent={"flex-start"}>
                        {publishedKBs.map((kb) => (
                            <Box
                                key={kb.kbId}
                                sx={{
                                    cursor: 'pointer',
                                    width: isMobile ? '100%' : '48%',
                                    padding: 1,
                                    marginTop: isMobile ? 1 : 2,
                                    marginBottom: isMobile ? 0 : 2,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                }}
                                onClick={(e) => handleNavigateWrapper(e, `/install/${kb.kbId}?publishedKbId=${kb.kbId}&publishedTitle=${encodeURIComponent(kb.kbTitle || '')}`)}
                            >
                                <Card
                                    sx={{
                                        display: 'flex',
                                        width: imageSize,
                                        marginRight: 2,
                                        flexShrink: 0,
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        src={`${RAW_FILES_URL}/kb-image/${kb.kbId}.png`}
                                        alt={kb.kbTitle || 'KB'}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                </Card>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        height: imageSize,
                                        flexGrow: 1
                                    }}
                                >
                                    <Typography variant="subtitle1"
                                                sx={{ color: colorText, textAlign: 'left', fontWeight: isMobile ? 700 : 'inherit', fontSize: isMobile ? 12 : undefined, lineHeight: '1.4' }}>
                                        {kb.kbTitle || 'Untitled KB'}
                                    </Typography>
                                    {kb.kbDescription && (
                                        <Typography variant="body2"
                                                    sx={{ lineHeight:'1.3', color: colorText, textAlign: 'left', fontSize: isMobile ? 12 : undefined }}>
                                            {kb.kbDescription.trim()}
                                        </Typography>
                                    )}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        width: '100%'
                                    }}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={(e) => handleNavigateWrapper(e, `/install/${kb.kbId}?publishedKbId=${kb.kbId}&publishedTitle=${encodeURIComponent(kb.kbTitle || '')}`)}
                                            sx={{
                                                fontSize: '0.75rem',
                                                minHeight: '24px',
                                                height: '24px',
                                                width: '100px',
                                            }}
                                        >
                                            <span>Install</span>
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </>
            )}

            {/* Show loader for blueprints only if no parkedDomain */}
            {!parkedDomain && <LocalLoader open={isLoading} />}

            {/* AI Blueprints Section - Only show if no parkedDomain and data is loaded */}
            {!parkedDomain && !isLoading && (
                <>
                    <Typography variant="overline" display="block"
                                style={{color: colorText, opacity: 0.5, fontSize: 12, letterSpacing: 2, marginTop: 20, marginBottom: 12}}>
                        Open Source AI Blueprints
                    </Typography>
                    <Box mt={-2} display="flex" flexWrap="wrap" justifyContent={"flex-start"}>
                        {apps.map((app) => {
                    const [title, _] = app.kbTitle.split(':');
                    const description = app.kbDescription;
                    return (
                        <Box
                            onClick={(e) => handleNavigateWrapper(e, `/install/${app.appId}`)}
                            key={app.appId}
                            data-testid={`app-install-${app.appId}`}
                            sx={{
                                cursor: 'pointer',
                                width: isMobile ? '100%' : '48%',
                                padding: 1,
                                marginTop: isMobile ? 1 : 2,
                                marginBottom: isMobile ? 0 : 2,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                            }}
                        >
                            <Card
                                sx={{
                                    display: 'flex',
                                    width: imageSize,
                                    marginRight: 2,
                                    flexShrink: 0,
                                }}
                            >
                                <Link to={`/install/${app.appId}`} style={{ textDecoration: 'none', width: '100%', height: '100%' }}>
                                    <CardMedia
                                        component={KBImageComponent}
                                        height={'100%'}
                                        width={'100%'}
                                        folder={'app-image'}
                                        alt={title}
                                        id={app.appId}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                </Link>
                            </Card>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    height: imageSize,
                                    flexGrow: 1
                                }}
                            >
                                <Typography variant="subtitle1"
                                            sx={{ color: colorText, textAlign: 'left', fontWeight: isMobile ? 700 : 'inherit', fontSize: isMobile ? 12 : undefined, lineHeight: '1.4' }}>
                                    {title}
                                </Typography>
                                {description && (
                                    <Typography variant="body2"
                                                sx={{ lineHeight:'1.3', color: colorText, textAlign: 'left', fontSize: isMobile ? 12 : undefined }}>
                                        {description.trim()}
                                    </Typography>
                                )}
                                {!isMobile && <Typography variant="body2" sx={{ marginBottom: '-4px',color: colorGray, alignSelf: 'flex-start', textTransform: 'capitalize', fontSize:12 }}>
                                    {app.category}
                                </Typography>}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontSize: iconFontSize,
                                    position: 'relative',
                                    width: '100%'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <ThumbUpOffAltIcon fontSize="small" style={{color:colorGray, fontSize: iconFontSize}} />
                                        <Typography variant="body2" sx={{ color: colorGray, marginRight: 1, fontSize: iconFontSize, marginTop: '2px' }}>
                                            {formatNumber(app.likes)}
                                        </Typography>
                                        <DownloadForOfflineOutlinedIcon fontSize="small" style={{color:colorGray, fontSize: iconFontSize}} />
                                        <Typography variant="body2" sx={{ color: colorGray, fontSize: iconFontSize, marginTop: '2px' }}>
                                            {formatNumber(app.downloads)}
                                        </Typography>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={(e) => handleNavigateWrapper(e, `/install/${app.appId}`)}
                                        sx={{
                                            fontSize: '0.75rem',
                                            minHeight: '24px',
                                            height: '24px',
                                            width: '100px',
                                            position: 'absolute',
                                            right: 0
                                        }}
                                    >
                                        <span>Clone Agent</span>
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
                    </Box>
                </>
            )}
        </Container>
    );
};